'use client';

import { Fragment, useEffect, useState } from 'react';

import { useCountdownDate } from '@flipster/react/design-utils/countdown-date';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { Styles, css } from '@flipster/styled-system/css';
import { hstack, vstack } from '@flipster/styled-system/patterns';

import { CountdownItemFragment } from '@web/app/[locale]/(web)/campaign/query.generated';

interface CountdownProps {
  targetDate: string;
  overText?: string;
  onEnd?: () => void;
}

const countdownList: ['dd' | 'hh' | 'mm' | 'ss', string][] = [
  ['dd', 'Days'],
  ['hh', 'Hrs'],
  ['mm', 'Mins'],
  ['ss', 'Secs'],
];

export const CountdownDate = ({ overText, ...props }: CountdownProps) => {
  const isMounted = useMounted();
  const countdownData = useCountdownDate({
    maxUnit: 'dd',
    minUnit: 'ss',
    ...props,
  });

  const className = hstack.raw({ wordBreak: 'keep-all' });

  if (!isMounted) return <div className={css(className)} translate='no' />;
  if (countdownData === null && overText) {
    return (
      <div className={css(className, { color: 'content.state.warning' })} translate='no'>
        {overText}
      </div>
    );
  }
  return (
    <div className={css(className, { textStyle: 'Display/L', color: 'inverse.white' })} translate='no'>
      {countdownList.map(([valueKey, unit], index) => (
        <Fragment key={`${unit}__countdown`}>
          {index !== 0 && <span className={css({ mx: { base: 14, tablet: 18 } })}>:</span>}
          <div className={vstack({ gap: 2 })}>
            <p className={css({ fontVariantNumeric: 'tabular-nums' })}>
              {String(countdownData?.[valueKey] ?? 0).padStart(2, '0')}
            </p>
            <span
              className={css({
                textStyle: 'Text/M/Regular',
                color: 'content.default.level-3',
              })}
            >
              {unit}
            </span>
          </div>
        </Fragment>
      ))}
    </div>
  );
};

interface CountdownsForContentfulProps {
  countdowns: (CountdownItemFragment | null)[];
  css?: Styles;
}

export const CountdownsForContentful = ({ countdowns, css: cssProps }: CountdownsForContentfulProps) => {
  const [index, setIndex] = useState(-1);
  const [isOver, setIsOver] = useState(false);

  const isMounted = useMounted();

  useEffect(() => {
    if (!countdowns || countdowns.length === 0) return;
    const now = new Date();
    let activeIndex = -1;
    for (let i = 0; i < countdowns.length; i++) {
      const countdown = countdowns[i];
      if (countdown?.date && new Date(countdown.date) > now) {
        activeIndex = i;
        break;
      }
    }
    if (activeIndex === -1 && countdowns.length > 0) {
      activeIndex = countdowns.length - 1;
      setIsOver(true);
    } else {
      setIsOver(false);
    }
    setIndex(activeIndex);
  }, [countdowns]);

  const handleCountdownEnd = () => {
    const nextIndex = index + 1;
    if (nextIndex >= countdowns.length) {
      setIsOver(true);
    } else {
      setIndex(nextIndex);
    }
  };

  if (!isMounted) return null;

  const currentData = countdowns[index];
  if (!currentData) return null;

  return (
    <div className={css(cssProps)}>
      {!isOver && <span>{currentData.label}</span>}
      <CountdownDate
        targetDate={new Date(currentData.date).toISOString()}
        overText={currentData?.afterText || 'The campaign has ended'}
        onEnd={handleCountdownEnd}
      />
    </div>
  );
};
